import { useCallback, useRef } from 'react'
import { isTouchDevice } from '../services/device'

export interface UseTouchHintOptions {
    onLongPress: () => void
    longPressDelay?: number
    moveThreshold?: number
}

/**
 * A hook to detect long-presses on touch devices while allowing normal clicks to function.
 * Distinguishes between mouse and touch to avoid breaking PC logic.
 */
export function useTouchHint({ onLongPress, longPressDelay = 500, moveThreshold = 10 }: UseTouchHintOptions) {
    const isTouchPossible = isTouchDevice()
    const timerRef = useRef<NodeJS.Timeout | null>(null)
    const startPosRef = useRef<{ x: number, y: number } | null>(null)
    const isLongPressTriggered = useRef(false)

    const handlePointerDown = useCallback((e: React.PointerEvent) => {
        // Only track long-press for touch/pen pointers. Mouse should use hover.
        if (e.pointerType === 'mouse') return

        isLongPressTriggered.current = false
        startPosRef.current = { x: e.clientX, y: e.clientY }

        if (timerRef.current) clearTimeout(timerRef.current)
        timerRef.current = setTimeout(() => {
            isLongPressTriggered.current = true
            onLongPress()
        }, longPressDelay)
    }, [onLongPress, longPressDelay])

    const handlePointerMove = useCallback((e: React.PointerEvent) => {
        if (e.pointerType === 'mouse' || !startPosRef.current) return

        const dist = Math.sqrt(
            Math.pow(e.clientX - startPosRef.current.x, 2) +
            Math.pow(e.clientY - startPosRef.current.y, 2)
        )

        if (dist > moveThreshold) {
            if (timerRef.current) {
                clearTimeout(timerRef.current)
                timerRef.current = null
            }
        }
    }, [moveThreshold])

    const handlePointerUp = useCallback((e: React.PointerEvent) => {
        if (e.pointerType === 'mouse') return
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
    }, [])

    const handlePointerCancel = useCallback((e: React.PointerEvent) => {
        if (timerRef.current) {
            clearTimeout(timerRef.current)
            timerRef.current = null
        }
        startPosRef.current = null
    }, [])

    /**
     * Helper to intercept the click event if it followed a long-press.
     * Returns true if the click should be ignored.
     */
    const interceptClick = useCallback((e: React.MouseEvent) => {
        if (isLongPressTriggered.current) {
            e.preventDefault()
            e.stopPropagation()
            isLongPressTriggered.current = false // Reset for next interaction
            return true
        }
        return false
    }, [])

    if (!isTouchPossible) {
        return {
            onPointerDown: undefined,
            onPointerMove: undefined,
            onPointerUp: undefined,
            onPointerCancel: undefined,
            interceptClick: () => false,
            style: {}
        }
    }

    return {
        onPointerDown: handlePointerDown,
        onPointerMove: handlePointerMove,
        onPointerUp: handlePointerUp,
        onPointerCancel: handlePointerCancel,
        interceptClick,
        style: { touchAction: 'pan-y' } as React.CSSProperties
    }
}
